import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BatchReturn, ReturnOrderLine } from 'returnCenter-api';
import styled from 'styled-components';

import Button from 'components/ui/Button';
import Flex from 'components/ui/Flex';
import RightDrawer from 'components/ui/Drawer/RightDrawer';
import Text from 'components/ui/Text';
import { HandleReturnItemChange } from 'components/Orders/Order';
import { ExtendedReturnItem } from 'store/reducers/shared/sliceTypes';
import CreateBatchReturnModal from 'components/Orders/CreateBatchReturnModal';

export type BatchDrawerTarget = {
    orderNumber: string;
    orderline: ReturnOrderLine;
    returnItem: ExtendedReturnItem;
};

type BatchReturnDrawerProps = {
    open: boolean;
    onClose: () => void;
    target: BatchDrawerTarget | null;
    disabled?: boolean;
    handleReturnItemChange: HandleReturnItemChange;
    batchDestinationNames: Record<number, string>;
    openBatchReturns: BatchReturn[];
};

const BatchReturnDrawer = ({
    open,
    onClose,
    target,
    disabled = false,
    handleReturnItemChange,
    batchDestinationNames,
    openBatchReturns,
}: BatchReturnDrawerProps) => {
    const { t } = useTranslation();
    const [selectedBatchReturnId, setSelectedBatchReturnId] = useState<number | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);

    useEffect(() => {
        if (open && target) {
            setSelectedBatchReturnId(target.returnItem.batchReturnId ?? null);
        }
    }, [open, target]);

    const destinationId = target?.returnItem.batchReturnDestinationId;
    const destinationName = destinationId ? batchDestinationNames[destinationId] ?? '' : '';

    const filteredBatchReturns = openBatchReturns.filter(
        (br) => br.batchDestinationId === destinationId,
    );

    const handleSave = () => {
        if (!target) return;
        const { orderNumber, orderline, returnItem } = target;
        const sku = orderline.sku ?? '';
        const orderLineNumber = orderline.orderLineNumber ?? '';
        const itemId = returnItem.id ?? returnItem.splitId ?? -1;

        if (selectedBatchReturnId !== (returnItem.batchReturnId ?? null)) {
            handleReturnItemChange(orderNumber, sku, orderLineNumber, itemId, 'batchReturnId', selectedBatchReturnId);
        }
        onClose();
    };

    const handleCreated = (batchReturn: BatchReturn) => {
        if (batchReturn?.id) {
            setSelectedBatchReturnId(batchReturn.id);
        }
    };

    const productName = target?.orderline.productName ?? '';
    const sku = target?.orderline.sku ?? '';

    return (
        <RightDrawer
            open={open}
            onClose={onClose}
            title={t('batchReturn.assignBatch', 'Assign to Batch Return')}
            footer={
                <Flex justify="end" gap="0.5rem">
                    <Button rounded onClick={onClose} type="button">
                        {t('cancel')}
                    </Button>
                    <Button rounded main onClick={handleSave} type="button" disabled={disabled}>
                        {t('save')}
                    </Button>
                </Flex>
            }
        >
            <Flex column gap="1rem">
                {target && (
                    <>
                        <Flex column gap="0.25rem">
                            <Text fontSize={14} bold>
                                {productName}
                            </Text>
                            <Text fontSize={12} color="textFaded">
                                {sku}
                            </Text>
                        </Flex>

                        <Flex column gap="0.25rem">
                            <Label>{t('batchReturn.destination', 'Destination')}</Label>
                            <Text fontSize={14}>{destinationName}</Text>
                        </Flex>

                        <Flex column gap="0.25rem">
                            <Label>{t('batchReturn.selectBatchReturn', 'Batch Return')}</Label>
                            <Select
                                value={selectedBatchReturnId ?? ''}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setSelectedBatchReturnId(val ? Number(val) : null);
                                }}
                                disabled={disabled}
                            >
                                <option value="">{t('batchReturn.notAssigned', '-- Not assigned --')}</option>
                                {filteredBatchReturns.map((br) => (
                                    <option key={br.id} value={br.id}>
                                         #{br.id} {br.batchDestinationName} {br.comment ? `(${br.comment})` : ''} ({new Date(br.created!).toLocaleDateString()})
                                    </option>
                                ))}
                            </Select>
                        </Flex>

                        <Button
                            rounded
                            onClick={() => setShowCreateModal(true)}
                            type="button"
                            disabled={disabled || !destinationId}
                        >
                            {t('batchReturn.createNew', 'Create new batch return')}
                        </Button>
                    </>
                )}
            </Flex>

            <CreateBatchReturnModal
                open={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreated={handleCreated}
                preselectedDestinationId={destinationId ?? undefined}
            />
        </RightDrawer>
    );
};

export default BatchReturnDrawer;

const Label = styled(Text).attrs({ fontSize: 12, bold: true })`
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.colors.textFaded};
`;

const Select = styled.select`
    padding: 0.5rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 4px;
    font-size: 14px;
`;
